/**
 * The four finding-bearing Scoring reads, judged ONCE.
 *
 * Everyone who reports Scoring outside its own tab starts here: the rail dot
 * (`scoringRail`, in Scoring.tsx) and the verdict band (`scoringFindings`, in
 * Triage.tsx). They share this judgement so they cannot disagree about which
 * findings count or which reads are blind. It is deliberately a neutral module,
 * not part of the Scoring tab: Triage must not import a pipeline's panel to
 * learn what that pipeline's reads mean.
 *
 * A read that is still loading is neither findings nor blind: it has nothing to
 * say yet. A read that FAILED is blind, and its previous payload is not current
 * (react-query keeps the last good `data` after a failed refetch) -- so its
 * findings are withheld rather than shown as if they were fresh (ruling R10).
 */
import type {
  ScoringFailures, ScoringFinding, ScoringJobs, ScoringOutcomes, ScoringOverview,
} from '../api/schemas'

/** What a query hands over: the payload (the PREVIOUS one, if a refetch failed)
 *  and whether the latest read failed. */
export type Read<T> = { data?: T | undefined; isError: boolean }

/** The four finding-bearing endpoints, in the order every consumer ranks them
 *  (spec §7: "crit above warn, then by endpoint order"). */
export const SCORING_READS = ['overview', 'outcomes', 'jobs', 'failures'] as const
export type ScoringReadKey = (typeof SCORING_READS)[number]

/** One endpoint, as the rail and Triage both judge it. */
export type ScoringReading = {
  key: ScoringReadKey
  /** `/metrics/scoring/<key>` -- what an unreadable read is named by. */
  path: string
  /** The latest read failed: the board is blind to this endpoint. */
  unread: boolean
  /** The endpoint's findings -- EMPTY when unread. */
  findings: readonly ScoringFinding[]
}

export function scoringReads(
  overview: Read<ScoringOverview>, outcomes: Read<ScoringOutcomes>,
  jobs: Read<ScoringJobs>, failures: Read<ScoringFailures>,
): ScoringReading[] {
  const reads: Array<Read<{ findings: ScoringFinding[] }> | undefined> = [overview, outcomes, jobs, failures]
  return SCORING_READS.map((key, i) => {
    const r = reads[i]
    const unread = r?.isError === true
    return {
      key, path: `/metrics/scoring/${key}`, unread,
      findings: unread ? [] : r?.data?.findings ?? [],
    }
  })
}
